import { Injectable, inject } from '@angular/core';
import { DynamicFlowService } from './dynamic-flow.service';
import { ContextEngineService } from './context-engine.service';
import { DefaultAgentCardsService } from './default-agent-service';
import { GlobalToolsService } from './global-tools.service';
import { ChatRole, ContextType } from '../models/agent.models';
import { MOOD_STATE_PROMPT } from '../prompts/dynamic-criteria.prompts';
import { MessagesStateService } from './messages-state.service';
import { MoodStateOptions } from '@dataclouder/ngx-core';

@Injectable({
  providedIn: 'root',
})
export class DynamicCriteriaService {
  private dynamicFlowService = inject(DynamicFlowService);
  private contextEngineService = inject(ContextEngineService);
  private defaultAgentCardService = inject(DefaultAgentCardsService);
  private globalToolsService = inject(GlobalToolsService);
  private messageState = inject(MessagesStateService);

  public async evaluateUsingTools() {
    const tools = this.dynamicFlowService.conversationFlowState()?.tools || [];

    const enabledTools = tools.filter((tool) => tool?.enabled || false);

    if (!enabledTools.length) {
      return;
    }

    let listString = '';
    for (const tool of enabledTools) {
      listString += `${tool.name}: ${tool.description}\n`;
    }

    const conversationContext = this.contextEngineService.getConversationContext(ContextType.Last2Messages);
    const finalPrompt = `
        Evaluate using the context and return only JSON if condition is met similar to this:
        {
        "tool1": true,
        "tool2": false,
        }
        <Context>
        ${conversationContext}
        </Context>
        <Tools>
        ${listString}
        </Tools>
        return only JSON with true or false for the tools
        `;

    const result = await this.defaultAgentCardService.callChatCompletion({
      messages: [{ role: ChatRole.User, content: finalPrompt }],
      returnJson: true,
    });

    if (result.content?.['ends']) {
      console.log('Tools result:', result.content);
      console.log('Using tool ends');

      this.globalToolsService.useTool('ends', result.content);
    }
  }

  public getMoodEmoji(mood: string): string | undefined {
    if (!mood) {
      return undefined;
    }
    const moodState = MoodStateOptions.find((option) => option.value === mood);
    return moodState?.emoji;
  }

  public async evaluateMoodState(): Promise<string[]> {
    // Se asume que esto ocurré después de  que el asistente responde. entonces el ultimo mensaje debe ser del asistente.
    const last2Dialog = this.contextEngineService.getContextMessages(ContextType.Last2Messages);
    const conversationContext = last2Dialog.map((message) => `${message.role}: ${message.content}`).join('\n');
    const assistantMessage = last2Dialog.find((message) => message.role === ChatRole.Assistant);

    const prompt = `${MOOD_STATE_PROMPT}\n\n<Context>${conversationContext}</Context>`;

    const result = await this.defaultAgentCardService.callChatCompletion({
      messages: [{ role: ChatRole.User, content: prompt }],
      returnJson: true,
    });

    if (Array.isArray(result.content)) {
      console.log('Mood state result:', result.content, assistantMessage);
      if (assistantMessage?.messageId) {
        const moodEmojis = result.content.map((mood) => this.getMoodEmoji(mood)).filter((emoji): emoji is string => !!emoji);
        this.messageState.updateMessage(assistantMessage.messageId, { tags: moodEmojis });
      }

      return result.content;
    } else {
      console.error('Something is wrong with mood state result:', result.content);
      return [];
    }
  }
}
